
import { Component, Vue } from 'vue-property-decorator';
import { AuthUser } from '@/models/authUser';
import { LessonPlan } from '@/models/lessonPlan';

@Component
export default class ShareModal extends Vue {
  private loading = false;
  private shares = [] as string[];
  private selectedCoach = [];

  created() {
    if (this.plan.shares) {
      this.shares = [...this.plan.shares];
    }
  }

  get plan(): LessonPlan {
    return this.$store.getters['lessonPlans/selectedPlan'];
  }

  get coaches() {
    return this.$store.getters['lessonPlans/coaches'];
  }

  get coachList() {
    return this.coaches
      .map((coach: AuthUser) => {
        return {
          value: coach.uuid,
          text: coach.firstname + ' ' + coach.lastname,
        };
      })
      .filter((item: any) => !this.shareList.find(share => share.uuid === item.value));
  }

  get shareList() {
    const shares = this.shares.reduce((results: Array<any>, uuid: string) => {
      let name = '';
      const user = this.coaches.find((c: AuthUser) => c.uuid === uuid);
      if (user) {
        // Don't show users which have been deleted in the list
        name = user.firstname + ' ' + user.lastname;
        results.push({
          uuid,
          name,
        });
      }
      return results;
    }, []);

    if (this.plan) {
      shares.unshift({
        uuid: this.plan.owner.uuid,
        name: `${this.plan.owner.firstname} ${this.plan.owner.lastname} - ${this.$t('planning.owner')}`,
      });
    }

    return shares;
  }

  get ownerUuid() {
    return this.plan.owner.uuid;
  }

  get readOnly() {
    return this.plan.readonly === 1;
  }

  get allowSave() {
    // If plan is shared with you, only allow save if removing self
    if (this.readOnly) {
      return !!this.shares.length;
    }
    return false;
  }

  removeShare(uuid: string) {
    this.shares = this.shares.filter(v => v !== uuid);
  }

  addShare(uuid: string) {
    if (!this.shares.includes(uuid)) {
      this.shares.push(uuid);
    }
    this.selectedCoach = [];
  }

  async save() {
    try {
      // User has removed themselves from the shared plan, they can't add users so just send the owner uuid
      let removedSelf = false;
      if (!this.shares.length && this.readOnly) {
        const res = await this.$confirm(this.$t('planning.confirm_remove_self') as string, {
          title: this.$t('global.are_you_sure') as string,
          color: 'dark',
          icon: 'mdi-help-circle-outline',
          buttonTrueText: this.$t('global.ok') as string,
          buttonFalseText: this.$t('global.cancel') as string,
        });
        if (!res) {
          return;
        }
        removedSelf = true;
      }
      this.loading = true;
      await this.$store.dispatch('lessonPlans/updateShares', this.shares);
      if (!removedSelf) {
        await this.$store.dispatch('storeDb/fetchAndSaveLessonPlan', this.plan.uuid); // Update the details for this specific plan
        await this.$store.dispatch('lessonPlans/fetchLessonPlanFromDb', this.plan.uuid); // Fetch updated plan
      } else {
        await this.$store.dispatch('storeDb/deleteLessonPlan', this.plan.uuid); // Remove from db since they no longer have access
        this.$router.push({ name: 'planning' }).catch(() => {
          // ignore routing errors
        });
      }
      this.shares = [];
      this.$alert('success', this.$t('planning.shares_updated'));
      this.$emit('close');
    } catch {
      this.$alert('error', this.$t('planning.shares_not_updated'));
    }
    this.loading = false;
  }
}
